import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams, Redirect } from 'react-router-dom'
import queryString from 'query-string'

import {
  fetchAgentConfig,
  fetchAgentConfigByUserId,
  fetchChangeRequests,
} from '@/reducers/voiceAgents/voiceAgents.actions'
import { setSelectedAgent, focusChangeRequest } from '@/reducers/voiceAgents/voiceAgents.redux'
import FourOhFour from '@/views/Auth/FourOhFour'

import { parseChangeRequestFocus } from './changeRequestHelpers'
import { WebCallProvider } from './hooks/WebCallContext'
import AgentHeader, { resolvePageTab } from './AgentHeader'
import CanvasView from './CanvasView'
import NodeDrawerDock from './components/NodeDrawerDock'
import ChangeRequestsPage from './components/ChangeRequestsPage'
import OverviewTab from './components/OverviewTab'
import ConnectorCredentialsPage from './components/ConnectorCredentialsPage'
import WebCallScreen from './components/WebCallScreen'

import './VoiceAgents.scss'
import './components/ConnectorCredentialsPage.scss'

const VoiceAgentsPage = () => {
  // The route matches either `/voice-agents/:agentId(.+)` (legacy) or
  // `/voice-agents/by-user/:userId(\\d+)` (new). Exactly one param is set
  // per render — branch on whichever is present.
  const { agentId, userId } = useParams()
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()
  const agentsLoading = useSelector((state) => Boolean(state.voiceAgents.loading.agents))
  const selectedAgentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const agentNotFound = useSelector((state) => state.voiceAgents.agentNotFound)
  const activeTab = resolvePageTab(location.search)

  // Deep-link focus (copy-link URLs): parsed once on mount and consumed only
  // after setSelectedAgent has run, so the agent-change drawer reset can't
  // wipe the focus state. The params are stripped from the URL on consumption.
  const pendingFocusRef = useRef(parseChangeRequestFocus(location.search))

  useEffect(() => {
    const consumePendingFocus = () => {
      if (!pendingFocusRef.current) {
        return
      }
      dispatch(focusChangeRequest(pendingFocusRef.current))
      pendingFocusRef.current = null
      // Read the pathname at call time: the by-user branch runs this after an
      // await, and the `location` captured at mount could be stale by then.
      history.replace({
        pathname: history.location.pathname,
        search: queryString.stringify({ tab: 'call-flow' }),
      })
    }
    if (userId) {
      // by-user path: BE returns the full agent config; the thunk resolves
      // the agent_id from the response and we sync selectedAgentId so the
      // canvas/drawer selectors see the right detail.
      ;(async () => {
        // Clear any previously-selected agent up front so we never flash the
        // last agent's data while this one loads — and, if this agent has no
        // externalized prompts (404), the canvas shows the empty state instead
        // of the stale previous agent.
        dispatch(setSelectedAgent(null))
        const resolvedAgentId = await dispatch(fetchAgentConfigByUserId(userId))
        if (resolvedAgentId) {
          dispatch(setSelectedAgent(resolvedAgentId))
          consumePendingFocus()
        }
      })()
    } else if (agentId) {
      // Selecting up front is safe even for a slug this caller doesn't own:
      // selectedAgentId is only ever a key into detailById, and the ownership
      // guard means no foreign detail is ever stored under it. So there's
      // nothing of another org's to flash — the header renders without a title
      // and the canvas its empty state until the config lands (or the 404
      // empty state replaces the page).
      dispatch(setSelectedAgent(agentId))
      dispatch(fetchAgentConfig(agentId))
      consumePendingFocus()
    }
  }, [dispatch, history, agentId, userId])

  useEffect(() => {
    if (selectedAgentId) {
      dispatch(fetchChangeRequests(selectedAgentId))
    }
  }, [dispatch, selectedAgentId])

  if (!agentId && !userId) {
    return <Redirect to="/voice-agents" />
  }

  // The deep-linked agent doesn't exist, is retired (its ai_agent owner is
  // soft-deleted), or belongs to another org — the BE answers 404 for all three
  // so slugs can't be enumerated. This is a dead URL, not an empty view of a
  // real agent, so it gets the app's own 404 page rather than agent chrome
  // wrapped around nothing.
  if (agentNotFound) {
    return <FourOhFour />
  }

  const renderTabContent = () => {
    if (activeTab === 'overview') {
      return <OverviewTab />
    }
    if (activeTab === 'change-requests') {
      return <ChangeRequestsPage />
    }
    if (activeTab === 'credentials') {
      return <ConnectorCredentialsPage />
    }
    return (
      <div className="voice-agents__canvas-area">
        <CanvasView agentsLoading={agentsLoading} />
        <NodeDrawerDock />
      </div>
    )
  }

  // When a connector credential editor is open (?tab=credentials&cc=…), hide the
  // agent header/tabs so it reads as a focused, full-page editor.
  const hideChrome = activeTab === 'credentials' && Boolean(queryString.parse(location.search).cc)

  return (
    // The provider wraps the page: the header opens the call view and the view
    // renders the call, so the hook that owns the mic and the socket has to sit
    // above both. The view itself covers the workspace rather than sitting
    // beside it — see WebCallScreen.
    <WebCallProvider>
      <div className="voice-agents" data-testid="voice-agents-page">
        <section className="voice-agents__workspace">
          {!hideChrome && <AgentHeader />}
          {renderTabContent()}
        </section>
        <WebCallScreen />
      </div>
    </WebCallProvider>
  )
}

export default VoiceAgentsPage
